
import {defineComponent} from 'vue';
import Tooltip from "@/components/Tooltip.vue";
import IconBase from "@/components/IconBase.vue";
import ServerError from "@/components/ServerError.vue";
import Button from "@/components/ergebnis/Button.vue";
import ErgebnisKompTO from "@/types/ErgebnisKompTO";
import {trackGAEvent} from "@/utils/TrackingUtil";
import EHinweis from '@/enums/EHinweis';
import numberMixin from "@/mixins/NumberMixin";

export default defineComponent({
    name: 'ErgebnisFinBestaetigung',
    components: {Tooltip, IconBase, ServerError, Button},
    mixins: [numberMixin],
    emits: ['checkIfFinanzierungPossible'],
    data() {
        return {
            gesamteffektivzins: 0.0,
            gesamtlaufzeit: "",
            immoWert: 0.0,
            monatlicheRate: 0.0,

            textKaufpreis: "",
            textFinanzierungNichtMoeglich: "",
            textMonatlicheRate: "",
            textTooltipMonatlicheRate: "",
            textButtonBestaetigung: "",
            textButtonKeineBestaetigung: "",
            textErrorHeading: "Anwendung nicht verfügbar",
            textErrorMessage: "Aufgrund technischer Wartungsarbeiten ist die Anwendung derzeit nicht verfügbar.",
            urlKontaktformularBestaetigung: "",
            urlKontaktformularKeineBestaetigung: ""
        };
    },
    props: {
        showServerError: {
            type: Boolean,
            required: false
        }
    },
    methods: {
        onButtonClicked(isFinanzierung: boolean): void {
            let url = '',
                berechnungsdetails = this.$store.state.berechnungsergebnis.berechnungsdetails,
                einnahmen = this.$store.state.sliderEinnahmen.value,
                ausgaben = this.$store.state.sliderAusgaben.value,
                eigenkapital = this.$store.state.sliderEigenkapital.value,
                gesamtlaufzeit = this.getGesamtlaufzeit(),
                sollzinsbindung = this.$store.state.selectedSollzinsbindung;

            let parameter = '?rechnerTyp= finanzierungsbestaetigung&'
                + ' kaufpreis= ' + this.convertNumberWithDigits(this.kaufpreis) + '&'
                + ' nettoeinnahmen= ' + this.convertNumberWithDigits(einnahmen) + '&'
                + ' ausgaben= ' + this.convertNumberWithDigits(ausgaben) + '&'
                + ' eigenkapital= ' + this.convertNumberWithDigits(eigenkapital) + '&'
                + ' immobilienpreis= ' + this.convertNumberWithDigits(this.kaufpreis) + '&'
                + ' effektiverjahreszins= ' + this.convertNumberWithDigits(berechnungsdetails.effektiverJahreszins) + '&'
                + ' gesamtkosten= ' + this.convertNumberWithDigits(berechnungsdetails.gesamtkosten) + '&'
                + ' rate= ' + this.convertNumberWithDigits(this.monatlicheRate) + '&'
                + ' eintragungskosten= ' + this.convertNumberWithDigits(berechnungsdetails.eintragungskostenGrundbuch) + '&'
                + ' gesamtlaufzeit= ' + gesamtlaufzeit + '&'
                + ' restschuld= ' + this.convertNumberWithDigits(berechnungsdetails.restschuldZinsbindungsende) + '&'
                + ' finanzierungsmodell= ' + berechnungsdetails.finanzierungsmodell + '&'
                + ' gesamtbetrag= ' + this.convertNumberWithDigits(berechnungsdetails.zuZahlenderGesamtbetrag) + '&'
                + ' nettodarlehen= ' + this.convertNumberWithDigits(berechnungsdetails.nettodarlehen) + '&'
                + ' sollzinssatz= ' + this.convertNumberWithDigits(berechnungsdetails.jaehrlicherSollzinssatz) + '&'
                + ' sollzinsbindung= ' + sollzinsbindung + ' J./0 Mon.&'
                + ' ratenanzahl= ' + berechnungsdetails.anzahlRaten + '&'
                + ' tilgungssatz= ' + this.convertNumberWithDigits(berechnungsdetails.tilgungssatz);

            if (isFinanzierung) {
                url = this.urlKontaktformularBestaetigung;
            } else {
                url = this.urlKontaktformularKeineBestaetigung;
            }

            window.location.href = url + parameter;
        },
        animateNumbers(): void {
            if (this.isCalculating) {
                this.immoWert = parseFloat(this.getRandomNumbers(this.kaufpreis.toString()));
                this.monatlicheRate = parseFloat(this.getRandomNumbers(this.monatlicheRate.toString()));
                requestAnimationFrame(this.animateNumbers)
            } else {
                this.immoWert = this.kaufpreis;
                this.monatlicheRate = this.ergebnis.monatlicheRate
            }
        },
        //Die Zahlen im String sollen animiert werden, der Text nicht
        getRandomNumbers(text: string): string {
            let regex = /\d/gi,
                result,
                stringSplitted = text.split('');
            while ((result = regex.exec(text))) {
                let randomDigit = Math.floor(Math.random() * 9) + 1
                stringSplitted[result.index] = randomDigit.toString();
            }
            return stringSplitted.join('');
        },
        getGesamtlaufzeit(): string {
            const anzahlJahre = this.ergebnis.moeglicheGesamtlaufzeit.jahre;
            const anzahlMonate = this.ergebnis.moeglicheGesamtlaufzeit.monate;
            return anzahlJahre + " J./" + anzahlMonate + " Mon.";
        },
    },
    computed: {
        //Lesen der Werte aus dem Store
        ergebnis(): ErgebnisKompTO {
            return this.$store.state.berechnungsergebnis.ergebnisWidgetTO;
        },
        kaufpreis(): number {
            return this.$store.state.sliderPreis.value;
        },
        isCalculating(): boolean {
            return this.$store.state.isCalculating;
        },
        isInitDataLoaded(): boolean {
            return this.$store.state.initDataLoaded;
        },
        isFinanzierung(): boolean {
            let sliderRate = this.$store.state.sliderRate;
            let isFinanzierungPossible = true;

            if (sliderRate.hinweis && sliderRate.hinweis.hinweisID == EHinweis.MONATLICHE_RATE_WURDE_ERHOEHT) {
                trackGAEvent(this.$store.state.rechnerNameGAEvents, "1_meldung_keine_finbestaetigung", "");
                isFinanzierungPossible = false;
            }
            this.$emit('checkIfFinanzierungPossible', isFinanzierungPossible);
            return isFinanzierungPossible;
        }
    },
    watch: {
        isCalculating: function (newVal) {
            if (newVal) {
                this.animateNumbers();
            }
        },
        kaufpreis() {
            this.immoWert = this.kaufpreis;
        },
        ergebnis() {
            this.monatlicheRate = this.ergebnis.monatlicheRate;
        },
        isInitDataLoaded(isLoaded: boolean): void {
            if (isLoaded) {
                this.textKaufpreis = this.$store.state.fachtexte["rechner.ergebnis.kaufpreis"];
                this.textFinanzierungNichtMoeglich = this.$store.state.fachtexte["fehlerhinweis.finanzierung.nicht.moeglich"];
                this.textMonatlicheRate = this.$store.state.fachtexte["rechner.ergebnis.monatliche.rate.finanzierungsbestaetigung"];
                this.textTooltipMonatlicheRate = this.$store.state.fachtexte["rechner.ergebnis.tooltip.monatliche.rate"];
                this.textButtonBestaetigung = this.$store.state.fachtexte["rechner.ergebnis.beratungsbutton.finanzierungsbestaetigung"];
                this.textButtonKeineBestaetigung = this.$store.state.fachtexte["rechner.ergebnis.beratungsbutton.keine.finanzierungsbestaetigung"];
                this.textErrorHeading = this.$store.state.fachtexte["rechner.ergebis.fehler.ueberschrift"];
                this.textErrorMessage = this.$store.state.fachtexte["rechner.ergebnis.fehlertext"];
                this.urlKontaktformularBestaetigung = this.$store.state.fachtexte["url.beratungsformular.finanzierungsbestaetigung"];
                this.urlKontaktformularKeineBestaetigung = this.$store.state.fachtexte["url.beratungsformular.keine.finanzierungsbestaetigung"];
            }
        }
    },
    mounted() {
        this.animateNumbers();
    }
});
